/*
 * Pure helpers behind the per-page metrics columns.
 *
 * Kept free of React and router imports so they can be unit-tested directly --
 * the same reason domainMetrics.js exists.
 */

/* Page documents are keyed by doc id, which encodes a nested path as
   "homesteading%2Fbuild-a-shed". The metrics query reports the decoded path,
   "homesteading/build-a-shed". Normalising both ends is what lets a nested page
   find its numbers at all -- the old per-page hits job skipped these outright. */
export const metricSlug = (slug) =>
  String(slug || '')
    .replace(/%2f/gi, '/')
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();

/* [{slug, hits, ...}] -> { "<normalised slug>": {…} } */
export const indexMetricsBySlug = (metricPages) => {
  const out = {};
  (metricPages || []).forEach((page) => {
    const key = metricSlug(page && page.slug);
    if (key) out[key] = page;
  });
  return out;
};

const EMPTY = {
  hits: 0, impressions: 0, clicks: 0,
  ads_per_hit: null, page_rpm: null, ecpm: null, earnings: null,
  earnings_source: 'unknown',
  hits_daily: {}, impressions_daily: {},
  views_tracked: 0, readers: 0, read_rate: null, read_median_s: null,
};

/* AdSense only reports pages above an impression threshold, so the tail shares
   the residual instead. An inferred RPM must never be mistaken for a measured
   one — that is the difference between "this page earns well" and "we assumed
   it earns like its neighbours". */
export const isEstimated = (m) => !!m && m.earnings_source === 'estimated';

/* A page with no row in the metrics result had no traffic in the window --
   that is a real zero, not missing data, so it reads as 0 rather than "—". */
export const metricsForPage = (index, page) => {
  if (!page) return EMPTY;
  const key = metricSlug(page.slug || page.id || page._id);
  return (index && index[key]) || EMPTY;
};

/* Money, but never a misleading "$0.00" when the figure is simply unknown. */
export const formatMoney = (value) =>
  value == null ? '—' : `$${Number(value).toFixed(2)}`;

export const formatRatio = (value) =>
  value == null ? '—' : Number(value).toFixed(2);

export const formatCount = (value) =>
  value == null ? '—' : Number(value).toLocaleString();

/* Read time in seconds as "40s" / "1m 20s" / "12m". Pings come in 10s steps,
   so seconds only matter while they are a meaningful share of the figure. */
export const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  const s = Math.round(Number(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return m >= 10 || rest === 0 ? `${m}m` : `${m}m ${rest}s`;
};

/* Below this many readers a page's median read time is one or two people's
   habits, so it is shown muted rather than as a figure to act on. */
export const READ_MIN_SAMPLE = 5;
